<div class="container-fluid pt-3 px-2">
  <div class="col-12 mb-2">
    <div class="row g-2 justify-content-between align-items-center">
      <div class="col-md-6 col-xl-2">
        <h5 class="fw-bold text-dark mb-0">
          {{ selectedFunnel?.name ?? ('kanban.kanban.selectFunnel' | translate) }}
          <i
            class="mdi mdi-pencil-outline ms-1 text-muted cursor-pointer"
            (click)="openFunnelForm(selectedFunnel)"
          >
          </i>
        </h5>
        <div
          class="d-flex flex-column"
          [matTooltip]="$safeNavigationMigration(selectedFunnel?.description)"
          [matTooltipDisabled]="
            (selectedFunnel?.description?.length ?? 0) <= 50
          "
        >
          <small class="text-info">
            {{ 'kanban.kanban.category' | translate }}: {{ selectedFunnel?.category?.name ?? "" }}
          </small>

          <small class="text-muted">
            {{
              (selectedFunnel?.description?.length ?? 0) > 50
                ? ($safeNavigationMigration(selectedFunnel?.description) | slice: 0 : 50) + "…"
                : (selectedFunnel?.description ?? ('kanban.kanban.noFunnelSelected' | translate))
            }}
          </small>
        </div>
      </div>

      <div class="col-md-6 col-xl-10 gap-2 d-flex justify-content-end">
        <!--
          Out-of-profile count for the current query (RF-18, AC-08) and the funnel
          overview toggle. Both sit beside the filters because they qualify the same
          working list.
        -->
        <!--
          The search field and the out-of-profile count stay in this row through the
          filter panel's `toolbar` template: the panel itself is the full-width bar
          below, and both read the one filter state it keeps.
        -->
        <ng-container *ngTemplateOutlet="filterPanel.toolbar" />

        <!--
          `align-items-stretch` overrides `.trend-button`'s flex-start, so these buttons take the
          row's height like the loose ones beside them (the search field sets it, 40px).
        -->
        <div
          class="btn-group trend-button gap-1 align-items-stretch"
          role="group"
          [attr.aria-label]="'kanban.kanban.viewModeGroup' | translate"
        >
          <button
            type="button"
            class="btn"
            [class.btn-primary]="viewMode === 'board'"
            [class.btn-outline-secondary]="viewMode !== 'board'"
            [matTooltip]="'kanban.kanban.kanbanView' | translate"
            (click)="viewMode = 'board'"
          >
            <i class="mdi mdi-view-column"></i>
          </button>
          <button
            type="button"
            class="btn"
            [class.btn-primary]="viewMode === 'list'"
            [class.btn-outline-secondary]="viewMode !== 'list'"
            [matTooltip]="'kanban.kanban.listView' | translate"
            (click)="viewMode = 'list'"
          >
            <i class="mdi mdi-format-list-bulleted"></i>
          </button>
          <!--
            Funnel overview toggle (RF-21). Collapsed by default: it answers the
            supervisor's weekly question, not the salesperson's daily one, and opening
            it by default would push the board down for everyone.
          -->
          @if (hasDealRadar) {
          <button
            type="button"
            class="btn"
            [class.btn-primary]="showOverview"
            [class.btn-outline-secondary]="!showOverview"
            [matTooltip]="'kanban.kanban.funnelOverviewToggle' | translate"
            [attr.aria-pressed]="showOverview"
            (click)="toggleOverview()"
          >
            <i class="mdi mdi-chart-timeline-variant"></i>
          </button>
          }
        </div>

        <button
          class="btn trend-button btn-primary fw-bold mdi mdi-filter-variant dropdown-toggle"
          [matMenuTriggerFor]="filterFunnel"
        >
          {{ selectedFunnel?.name ?? ('kanban.kanban.filterFunnel' | translate) }}
        </button>
        <mat-menu #filterFunnel="matMenu">
          @if (funnels?.length) {
            @for (funnel of funnels; track $index) {
              <button mat-menu-item (click)="selectFunnel(funnel)">
                {{ funnel.name }}
              </button>
            }
          } @else {
            <h6 class="text-muted m-1">{{ 'kanban.kanban.noResultsFound' | translate }}</h6>
          }
        </mat-menu>

        <div class="btn-group trend-button gap-1 align-items-stretch">
          <button
            class="btn btn-primary fw-bold"
            [matTooltip]="'kanban.kanban.importOpportunities' | translate"
            (click)="import()"
          >
            {{ 'kanban.kanban.import' | translate }}
          </button>
          <button class="btn btn-success fw-bold" (click)="openFunnelForm()">
            {{ 'kanban.kanban.add' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- The filter bar (2026-10-27): the applied filters on one line, each chip its own editor. -->
  <div class="col-12 mb-2">
    <app-filter-panel
      #filterPanel
      [definitions]="filterDefs ?? []"
      [selectedFunnel]="selectedFunnel ?? null"
      [shownCount]="shownCount"
      [totalCount]="totalCount"
    />
  </div>

  @if (loadingContent) {
    <content-loading-list />
  } @else if (funnels?.length > 0) {
    @if (selectedFunnel) {
      <!--
        The pipeline figures that used to sit here were removed on 2026-08-18: the CRM
        dashboard already presents open/won/lost with the same variation against the
        preceding period, plus conversion rate, average ticket and revenue breakdowns.
        Duplicating them above the board added a second source of truth for the same
        numbers.

        They now live inside the funnel diagnostic, which opens only when the user asks
        for it (RF-01, RF-02, RF-04 — see spec change log 2026-08-18).
      -->
      @if (showOverview) {
        <div class="col-12 mb-2">
          <app-funnel-overview
            [selectedFunnel]="selectedFunnel"
            (periodChange)="onOverviewPeriod($event)"
          ></app-funnel-overview>
        </div>
      }

      @if (viewMode === "board") {
        <div class="col-12">
          <div
            class="board d-flex"
            cdkDropList
            cdkDropListOrientation="horizontal"
            [cdkDropListData]="stages"
            (cdkDropListDropped)="onStageDrop($any($event))"
          >
            @for (stage of stages; track stage._id) {
              <app-stage-column
                [stage]="stage"
                [connectedDropLists]="connectedDropLists"
                [stageValue]="stageValue(stage._id)"
                [stageCount]="stageCount(stage._id)"
                [stageBaseCount]="stageBaseCount(stage._id)"
              />
            }

            <div class="btn-group trend-button">
              <button class="btn btn-light fw-bold" (click)="addStage()">
                <i class="mdi mdi-plus me-1"></i>
                {{ 'kanban.kanban.newStage' | translate }}
              </button>
            </div>
          </div>
        </div>
      } @else {
        <div class="col-12 mt-3">
          <app-card-list />
        </div>
      }
    }
  } @else {
    <empty-records-component
      [message]="'kanban.kanban.noFunnelCreated' | translate"
      [subMessage]="'kanban.kanban.noFunnelCreatedSub' | translate"
      [icon]="'mdi mdi-alert-circle-outline'"
      [labelButton]="'kanban.kanban.addFirstFunnel' | translate"
      (emitEvent)="openFunnelForm()"
    />
  }
</div>
